import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, NavLink } from "react-router-dom";
import {
  FaHome,
  FaBuilding,
  FaUsers,
  FaShareAlt,
  FaAddressBook,
  FaChartLine,
  FaUserFriends,
} from "react-icons/fa";
import "../assets/css/CustomerPortal.css";
import axiosInstance from "../components/api/axiosInstance";
import CustomerDashboard from "./CustomerDashboard";
import CustomerLeads from "./CustomerLeads";
import RegisterLead from "./RegisterLead";
import CustomerLeadView from "./CustomerLeadView";
import CustomerProfileView from "./CustomerProfileView";
import CustomerProfileEdit from "./CustomerProfileEdit";
import CustomerProfileLayout from "./CustomerProfileLayout";
import CustomerChangePassword from "./CustomerChangePassword";
import CustomerCompanyProfile from "./CustomerCompanyProfile";
import CustomerEmployees from "./CustomerEmployees";
import CustomerEmployeeView from "./CustomerEmployeeView";
import CustomerSocialAccounts from "./CustomerSocialAccounts";
import CustomerSocialAccountView from "./CustomerSocialAccountView";
import CustomerTenantCustomers from "./CustomerTenantCustomers";
import CustomerTenantCustomerView from "./CustomerTenantCustomerView";
import CustomerTenantClients from "./CustomerTenantClients";
import CustomerTenantClientView from "./CustomerTenantClientView";
import { clearAuthSessionStorage } from "../utils/clearSessionStorage";
import { getPortalSession, isPortalOwner } from "./portalSession";

const CTX_PATH = process.env.REACT_APP_API_CTX_PATH || "/cld-office";

const NAV_ITEMS = [
  { to: "/portal", end: true, label: "Dashboard", icon: FaHome },
  { to: "/portal/company-profile", label: "Company profile", icon: FaBuilding, ownerOnly: true },
  { to: "/portal/employees", label: "Employees", icon: FaUsers, ownerOnly: true },
  { to: "/portal/social-accounts", label: "Social accounts", icon: FaShareAlt },
  { to: "/portal/clients", label: "Clients", icon: FaAddressBook },
  { to: "/portal/leads", label: "Leads", icon: FaChartLine },
  { to: "/portal/customers", label: "Customers", icon: FaUserFriends },
];

function getCustomer() {
  return getPortalSession();
}

function companyInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function CustomerPortal() {
  const customer = useMemo(getCustomer, []);
  const portalOwner = isPortalOwner(customer);
  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => portalOwner || !item.ownerOnly),
    [portalOwner]
  );
  const [companyName, setCompanyName] = useState(customer.Company_Name || "");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/company");
        const name = res.data?.data?.Company_Name;
        if (active && name) setCompanyName(String(name).trim());
      } catch (_) {
        // Keep company name from login session if the request fails.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const brandName = companyName.trim() || "Cloud Connect";
  const brandInitial = companyInitials(brandName);
  const displayName =
    customer.Full_Name ||
    `${customer.First_Name || ""} ${customer.Last_Name || ""}`.trim() ||
    "Client";
  const initial = (displayName || "C").charAt(0).toUpperCase();

  const handleLogout = () => {
    clearAuthSessionStorage();
    window.location.href = CTX_PATH;
  };

  return (
    <div className="cportal">
      <aside className="cportal-sidebar">
        <div className="cportal-brand">
          <span className="cportal-brand-mark" title={brandName}>
            {brandInitial}
          </span>
          <span className="cportal-brand-text" title={brandName}>
            {brandName}
          </span>
        </div>
        <nav className="cportal-nav">
          {navItems.map(({ to, end, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={end} className="cportal-nav-link">
              <Icon className="cportal-nav-link-icon" aria-hidden="true" />
              <span className="cportal-nav-link-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="cportal-sidebar-footer">
          <NavLink to="/portal/profile" className="cportal-user-link">
            <span className="cportal-avatar" title={displayName}>
              {initial}
            </span>
            <span className="cportal-user-meta">
              <span className="cportal-user-name">{displayName}</span>
              <span className="cportal-user-sub">View profile</span>
            </span>
          </NavLink>
          <button type="button" className="cportal-signout" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </button>
        </div>
      </aside>

      <div className="cportal-main">
        <header className="cportal-topbar">
          <div className="cportal-topbar-title">Welcome, {displayName}</div>
        </header>

        <main className="cportal-content">
          <Routes>
            <Route path="/portal" element={<CustomerDashboard />} />
            <Route path="/portal/clients" element={<CustomerTenantClients />} />
            <Route path="/portal/clients/:id" element={<CustomerTenantClientView />} />
            <Route path="/portal/leads" element={<CustomerLeads mode="leads" />} />
            <Route path="/portal/customers" element={<CustomerTenantCustomers />} />
            <Route path="/portal/customers/:id" element={<CustomerTenantCustomerView />} />
            <Route path="/portal/leads/new" element={<RegisterLead />} />
            <Route path="/portal/leads/:id" element={<CustomerLeadView />} />
            <Route path="/portal/company-profile" element={portalOwner ? <CustomerCompanyProfile /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/employees" element={portalOwner ? <CustomerEmployees /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/employees/:id" element={portalOwner ? <CustomerEmployeeView /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/social-accounts" element={<CustomerSocialAccounts />} />
            <Route path="/portal/social-accounts/:id" element={<CustomerSocialAccountView />} />
            <Route path="/portal/profile" element={<CustomerProfileLayout />}>
              <Route index element={<CustomerProfileView />} />
              <Route path="edit" element={<CustomerProfileEdit />} />
              <Route path="change-password" element={<CustomerChangePassword />} />
            </Route>
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default CustomerPortal;
